import CancelIcon from "@mui/icons-material/Cancel"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CloseIcon from "@mui/icons-material/Close"
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement } from "react"

import cutOutBad from "../../../assets/images/cutOutBad.png"
import cutOutGood from "../../../assets/images/cutOutGood.png"
import AppButton from "../../buttons/appButton"
import ImageWithCaption from "../../dataDisplay/images/captionImage"
import CheckMarkList from "../../dataDisplay/lsits/checkMarkList"
import AppDialog from "../../dialog/appDialog"
import { AppCardHeader } from "../../surfaces/appCard"

type IImageTipsProps = {
    isOpen: boolean
    close: () => void
}

export const TIPS_PART_1 = [
    "Is clear and in focus",
    "Is taken within 1m of the cut-out",
    "Contains the whole cut-out",
    "Is centred on the cut-out",
]

export const TIPS_PART_2 = [
    "Is appropriately lit - use your flash if not",
    "Is no more than 5 mb",
    "PDFs are not accepted. Please convert to an image file such as PNG or JPEG",
    "Name of attachment is less than 45 characters",
]

export const CX_TIPS = [
    "Please provide a copy of the final circuit diagram and schedule of protection settings (This can be part of the final circuit diagram).",
    "Please name your files (prior to upload) in a way that makes it clear what they are!",
    "Files must be less than 5MB in size.",
    "If you need to upload larger files, try zipping them and uploading the zip.",
    "Name of attachment is less than 45 characters",
]

// Caption icons
const GoodIcon = (): ReactElement => <CheckCircleIcon />
const BadIcon = (): ReactElement => <CancelIcon />

const ImageTips = ({ isOpen, close }: IImageTipsProps): ReactElement => {
    const theme = useTheme()

    return (
        <AppDialog isOpen={isOpen}>
            <DialogTitle>
                <AppCardHeader
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                    title={
                        <>
                            Photo Upload{" "}
                            <Box
                                color={theme.palette.secondary.main}
                                display="inline"
                            >
                                Tips
                            </Box>
                        </>
                    }
                    subheader="Tips and tricks to ensure you provide a good image!"
                />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={4}>
                    <Typography>
                        We require a cut-out image as part of your application.
                        Use the below tips and tricks to help ensure you submit
                        a good quality picture!
                    </Typography>
                    <Stack
                        spacing={6}
                        direction={{ sm: "row", xs: "column" }}
                        justifyContent="center"
                    >
                        <ImageWithCaption
                            imageUrl={cutOutGood}
                            caption="Good image"
                            color={theme.palette.success.main}
                            Icon={GoodIcon}
                        />
                        <ImageWithCaption
                            imageUrl={cutOutBad}
                            caption="Bad image"
                            color={theme.palette.error.main}
                            Icon={BadIcon}
                        />
                    </Stack>
                    <Typography component="span">
                        Please ensure your image:
                        <CheckMarkList
                            items={[...TIPS_PART_1, ...TIPS_PART_2]}
                        />
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <AppButton
                    variant="outlined"
                    color="primary"
                    onClick={close}
                    endIcon={<CloseIcon />}
                >
                    Close
                </AppButton>
            </DialogActions>
        </AppDialog>
    )
}

export default ImageTips
